import {
  Button,
  DatePickerDropdown,
  Modal,
  Text,
  TextBadge,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { ReverseBehaviorDialog } from '@/pages/FarmerList/Energy/ReverseBehaviorDialog';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import {
  ApiEnergyCustomer,
  ApiEnergyCustomerBehavior,
} from '@api/interfaces/ApiEnergyCustomer';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { alpha, Divider, Stack, useTheme } from '@mui/material';
import { BehaviorCompletionSource } from '@shared/enums';
import { businessToday, formatLocalDate } from '@shared/utilities';
import { groupBy } from 'lodash';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

interface ManageBehaviorsModalProps {
  customerName: string,
  energyCustomer: ApiEnergyCustomer,
  onClose: () => void,
  readOnly: boolean,
}

export const ManageBehaviorsModal = ({
  customerName,
  energyCustomer,
  onClose,
  readOnly,
}: ManageBehaviorsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const today = businessToday();
  const [dates, setDates] = useState<Record<string, string>>({});
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});
  const [reversing, setReversing] = useState<ApiEnergyCustomerBehavior | null>(null);
  const { farmerId } = energyCustomer;
  const energy = energyCustomer.energy!;
  // The server files a completion under the program year containing its date.
  const earliestCompletionDay = energy.earliestProgramStartDate;
  const latestReversalDay = energy.programEndDate < today ? energy.programEndDate : today;

  const storeUpdatedCustomer = (updated: ApiEnergyCustomer) => {
    queryClient.setQueryData([QueryKeys.GET_ENERGY_CUSTOMER, farmerId], updated);
  };

  const { isLoading, mutate: completeBehavior, variables } = useMutation(
    ({ behaviorId, completedDate }: { behaviorId: string, completedDate: string }) => (
      EnergyApi.completeBehavior(farmerId, behaviorId, { completedDate })
    ),
    {
      onError: (error: { message?: string }, { behaviorId }) => {
        setDateErrors((current) => ({
          ...current,
          [behaviorId]: error.message || t(($) => $.energy.programModal.errors.generic),
        }));
      },
      onSuccess: (updated, { completedDate }) => {
        storeUpdatedCustomer(updated);
        // An earlier year's completion doesn't show on this year's list, so say where it went.
        openSnackbar(completedDate < energy.programStartDate
          ? t(($) => $.energy.manageBehaviors.completedInEarlierYear, { date: completedDate })
          : t(($) => $.energy.manageBehaviors.completed));
      },
    },
  );

  const markComplete = (behaviorId: string) => {
    const completedDate = dates[behaviorId] ?? today;
    setDateErrors((current) => ({ ...current, [behaviorId]: '' }));
    completeBehavior({ behaviorId, completedDate });
  };

  const completionLabel = (completion: NonNullable<ApiEnergyCustomerBehavior['completion']>) => {
    const date = completion.completedDate;
    const isImport = completion.source === BehaviorCompletionSource.Import;
    return completion.completedBy && !isImport
      ? t(($) => $.energy.manageBehaviors.completedBy, { date, name: completion.completedBy })
      : t(($) => $.energy.manageBehaviors.markedCompleted, { date });
  };

  const renderState = (behavior: ApiEnergyCustomerBehavior) => {
    if (behavior.completion) {
      return (
        <Stack alignItems="center" direction="row" spacing={2}>
          <Stack alignItems="center" direction="row" spacing={0.5}>
            <CheckCircle color="success" fontSize="small" />
            <Text category="body-small" testID={`behavior-completed-${behavior.id}`}>
              {completionLabel(behavior.completion)}
            </Text>
          </Stack>
          {!readOnly && (
            <Button
              color="error"
              onClick={() => setReversing(behavior)}
              size="small"
              testID={`behavior-reverse-${behavior.id}`}
              variant="text"
            >
              {t(($) => $.energy.manageBehaviors.reverse)}
            </Button>
          )}
        </Stack>
      );
    }
    if (readOnly) {
      return (
        <Text category="body-small" sx={{ color: 'text.secondary' }}>
          {t(($) => $.energy.manageBehaviors.pending)}
        </Text>
      );
    }
    return (
      <Stack alignItems="flex-start" direction="row" spacing={2}>
        <DatePickerDropdown
          errorMessage={dateErrors[behavior.id] || undefined}
          label={t(($) => $.energy.manageBehaviors.completedOn)}
          maxDate={today}
          minDate={earliestCompletionDay}
          onChange={(date) => setDates({
            ...dates,
            [behavior.id]: date ? formatLocalDate(date) : '',
          })}
          outOfRangeErrorMessage={t(($) => $.shared.inputValidation.dateOutOfRange, {
            end: today,
            start: earliestCompletionDay,
          })}
          testID={`behavior-date-${behavior.id}`}
          value={DateTime.fromISO(dates[behavior.id] ?? today).toJSDate()}
          width={200}
        />
        <Button
          disabled={dates[behavior.id] === ''}
          loading={isLoading && variables?.behaviorId === behavior.id}
          onClick={() => markComplete(behavior.id)}
          testID={`behavior-complete-${behavior.id}`}
          variant="outlined"
        >
          {t(($) => $.energy.manageBehaviors.markComplete)}
        </Button>
      </Stack>
    );
  };

  const behaviorsByTier = groupBy(energy.behaviors, 'tierName');

  return (
    <Modal
      acceptButton={(props) => (
        <Button {...props} onClick={onClose} testID="manage-behaviors-done">
          {t(($) => $.common.actions.done)}
        </Button>
      )}
      onClose={onClose}
      open
      stickyFooter
      subline={`${energy.programName} · ${customerName}`}
      testID="manage-behaviors-modal"
      title={readOnly
        ? t(($) => $.energy.profile.viewBehaviors)
        : t(($) => $.energy.profile.manageBehaviors)}
      width={640}
    >
      <Text category="title-medium">{t(($) => $.energy.manageBehaviors.statusTitle)}</Text>
      <Text category="body-medium" testID="manage-behaviors-summary">
        {t(($) => $.energy.manageBehaviors.summary, {
          completed: energy.completedCount,
          tier: energy.currentTier?.name ?? t(($) => $.customers.details.notEnrolled),
          total: energy.totalCount,
        })}
      </Text>
      {Object.entries(behaviorsByTier).map(([tierName, behaviors]) => (
        <Stack key={tierName}>
          <VSpacer size="6" />
          <Stack
            data-testid={`manage-behaviors-tier-${tierName}`}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              borderRadius: '4px',
              px: '12px',
              py: '8px',
            }}
          >
            <Text category="title-medium">{tierName}</Text>
          </Stack>
          {behaviors.map((behavior) => (
            <Stack data-testid={`behavior-row-${behavior.id}`} key={behavior.id}>
              <VSpacer size="3" />
              <Stack alignItems="center" direction="row" spacing={1}>
                <Text category="title-small">{behavior.name}</Text>
                {behavior.isRequiredForTier && (
                  <Stack data-testid={`behavior-required-${behavior.id}`}>
                    <TextBadge
                      backgroundColor={theme.palette.warning.main}
                      text={t(($) => $.energy.manageBehaviors.requiredFor, {
                        tier: behavior.tierName,
                      })}
                      textColor={theme.palette.warning.contrastText}
                    />
                  </Stack>
                )}
              </Stack>
              <Text category="body-small">{behavior.description}</Text>
              {!!behavior.sku && (
                <Text category="body-small" sx={{ color: 'text.secondary' }}>
                  {t(($) => $.energy.manageBehaviors.sku, { sku: behavior.sku })}
                </Text>
              )}
              <VSpacer size="2" />
              {renderState(behavior)}
              <VSpacer size="3" />
              <Divider />
            </Stack>
          ))}
        </Stack>
      ))}
      {!!reversing && (
        <ReverseBehaviorDialog
          behavior={reversing}
          customerName={customerName}
          farmerId={farmerId}
          latestDay={latestReversalDay}
          onClose={() => setReversing(null)}
          onReversed={storeUpdatedCustomer}
          programStartDate={energy.programStartDate}
        />
      )}
    </Modal>
  );
};
